import { isNumber } from 'utils';
import { higherThanValidation, lowerThanValidation } from '../validations';
import { INPUT_TYPE } from '../utils';

const decimalSeparator = { de: ',', en: '.' };

export const getInputElType = (type) => {
  if ([INPUT_TYPE.NUMBER, INPUT_TYPE.INTEGER, INPUT_TYPE.TEXT].includes(type))
    return INPUT_TYPE.TEXT;

  return type;
};

// in our forms, we always want number to have "." as decimal separator.
// Number("2,5") returns NaN, while
// Number("2.5") returns 2.5 (a valid number)
export const generateNumberParseAndFormat = (locale) => ({
  parse: (value) =>
    value &&
    value.replace(decimalSeparator[locale] || ',', decimalSeparator.en),
  format: (value) =>
    value && locale !== 'en'
      ? value.replace(decimalSeparator.en, decimalSeparator[locale] || ',')
      : value,
});

export const generateNumberFieldProps = ({ locale, translate, min, max }) => {
  const { parse, format } =
    locale !== 'en' ? generateNumberParseAndFormat(locale) : {};
  return {
    parse,
    format,
    validate: generateNumberValidation({ translate, min, max }),
  };
};

export const generateInputHandler = ({ type, max, locale, precision }) => {
  const handlers = {
    integer: integerInputHandlerWrapper(max),
    number: numberInputHandlerWrapper({ locale, precision }),
  };

  return handlers[type];
};

const numberInputHandlerWrapper =
  ({ locale, precision }) =>
  (event) =>
    (event.target.value = formatNumberString({
      input: event.target.value,
      decimalSeparator: decimalSeparator[locale] || '.',
      precision,
    }));

/**
 *  a helper function that takes any string value and normalize it to
 *  a valid number value.
 *
 * Params:
 *  input: custom string value,
 *  decimalSeparator: it should be "." for if locale is "en" otherwise ","
 *  precision: integer, represents a number of digit we allow after the
 *             decimal separator
 *
 * Returns: a valid number using provided decimalSeparator. The caller
 *          is responsible of making the separator in line with the
 *          current locale
 */
const formatNumberString = ({ input, decimalSeparator, precision }) => {
  // Remove all non-numeric and non-separator characters
  let cleanedInput = input.replace(
    new RegExp(`[^0-9${decimalSeparator}]`, 'g'),
    ''
  );

  // Ensure only one decimal separator exists
  const separatorIndex = cleanedInput.indexOf(decimalSeparator);
  if (separatorIndex !== -1) {
    // Slice the string to keep only the first decimal separator
    cleanedInput =
      cleanedInput.slice(0, separatorIndex + 1) +
      cleanedInput
        .slice(separatorIndex + 1)
        .replace(new RegExp(`\\${decimalSeparator}`, 'g'), '');
  }

  // If the string starts with a decimal separator, prepend '0'
  if (cleanedInput.startsWith(decimalSeparator)) {
    cleanedInput = '0' + cleanedInput;
  }

  // Handle precision (truncate digits after the decimal point if necessary)
  const [integerPart, fractionalPart] = cleanedInput.split(decimalSeparator);
  if (fractionalPart && precision > 0) {
    cleanedInput =
      integerPart + decimalSeparator + fractionalPart.slice(0, precision); // Truncate to the allowed precision
  }

  return cleanedInput;
};

const integerInputHandlerWrapper = (max) => (event) => {
  const { value } = event.target;

  // Remove all non-numeric characters.
  const sanitizedValue = value.replace(/[^0-9]/g, '');

  // Enforce max length based on the maximum allowed value.
  const maxLength = max ? Math.ceil(Math.log10(max)) : null;
  const truncatedValue =
    maxLength && sanitizedValue.length > maxLength
      ? sanitizedValue.slice(0, maxLength)
      : sanitizedValue;

  event.target.value = truncatedValue; // Update the input value.
};

// link fields by defaut have value "http://". we want to make sure that
// if the value is indeed "http://" we treat it as an empty field
const normalizeLinkInputValue = (inputString) => {
  const trimmedLink = inputString.trim();
  return trimmedLink === 'https://' ? '' : trimmedLink;
};

// a helper function that takes for values, and a list of link field names,
// ad return normalized values. In this case, we remove link strings that
// consist only from "https://"
export const normalizeLinkValues = (values, linkFields) => {
  const normalizedValues = { ...values };
  linkFields.forEach((field) => {
    const urlString = normalizedValues[field];
    if (urlString) {
      normalizedValues[field] = normalizeLinkInputValue(urlString);
    }
  });
  return normalizedValues;
};

export const generateNumberValidation = ({ min, max, translate }) => {
  const minValidation = isNumber(min)
    ? higherThanValidation({ translate, condition: min })
    : null;
  const maxValidation = isNumber(max)
    ? lowerThanValidation({ translate, condition: max })
    : null;
  return (value) => minValidation?.(value) || maxValidation?.(value);
};
